import React, { useState, useEffect, useRef } from "react";
import { connect } from 'react-redux';
import { logUI } from "../../api/andyliticsApi";
import * as Tools from "../../helpers/Tools";
import { capitalize, debounce } from 'lodash'
import moment from 'moment-timezone';

const ChatBot = (props) => {
    const [chatBotOpen, setChatBotOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [isFirstMessage, setIsFirstMessage] = useState(true);
    const [showChatBotByLocation, setShowChatBotByLocation] = useState(false);
    const [unseenMessages, setUnseenMessages] = useState(0);
    const [messagesArray, setMessagesArray] = useState([]);
    const [chatBotOpenForBusiness, setChatBotOpenForBusiness] = useState(false);
    let refIsMinimized = useRef(isMinimized);
    let refIsFirstMessage = useRef(isFirstMessage);
    let refLocation = useRef(props.location);
    let refIsSWHP = useRef(props.hasSWHPLinked);
    let refIsFC = useRef(props.hasFCLinked);
    let refSWHPId = useRef(props.swhpMemberId);
    let refFCId = useRef(props.fcMemberId);
    let refCrmSendResponse = useRef(props.crmSendResponse);
    let chatBotStart = moment.tz('America/Chicago').hours(props.omnichannelChatBotStart).minutes(0).seconds(0);
    let chatBotEnd = moment.tz('America/Chicago').hours(props.omnichannelChatBotEnd).minutes(0).seconds(0);

    const clickEvent = document.createEvent("MouseEvents");
    clickEvent.initEvent('click', true, true);

    useEffect(() => {
        if (props.enableOmnichannelChatBot && props.isActiveFeature_ChatBot) {
            checkIfChatBotOpenForBusiness();
        }
    }, [props.isActiveFeature_ChatBot]);

    useEffect(() => {
        refIsMinimized.current = isMinimized;
    });

    useEffect(() => {
        refIsFirstMessage.current = isFirstMessage;
    });

    useEffect(() => {
        refIsSWHP.current = props.hasSWHPLinked;
        refIsFC.current = props.hasFCLinked;
    }, [props.hasSWHPLinked, props.hasFCLinked]);

    useEffect(() => {
        refSWHPId.current = props.swhpMemberId;
        refFCId.current = props.fcMemberId;
    }, [props.swhpMemberId, props.fcMemberId]);

    useEffect(() => {
        refLocation.current = props.location;
    }, [props.location]);

    useEffect(() => {
        refCrmSendResponse.current = props.crmSendResponse;
    }, [props.crmSendResponse]);

    useEffect(() => {

        let showChatBot = checkLocation(props.location);
        setShowChatBotByLocation(showChatBot && props.location !== '/support');

        if (showChatBot && props.location == '/support' && props.enableOmnichannelChatBot && props.isActiveFeature_ChatBot && openChatBotProactively(props.location) && !chatBotOpen && !isMinimized) {
            // don't even show bot if there are support tickets -- it's confusing UI
            if (// non error state and no support tickets
                (props.crmMessages !== null && props.crmMessages && props.crmMessages.length === 0 && !props.crmIsFetching)
                // account for if crmMessage fetch errors
                || (!props.crmMessages && !props.crmIsFetching && props.crmError && props.crmError !== null)
            ) {
                setShowChatBotByLocation(true);
                setTimeout(() => {
                    startChat(false);
                }, 1500);
            }

        }
    }, [props.location, props.hasSWHPLinked, props.hasFirstCareLinked, props.crmMessages, props.crmIsFetching, props.crmError])

    useEffect(() => {
        if (props.loginStatus === "LOGOUT" || props.loginStatus === "USER_LOGGED_OUT") {
            let logString = getLogString('CloseBcLogout', false);
            if (logString) {
                logUI(logString, new Date(), Tools.getURLPath())
            }
            if (chatBotOpen) {
                Microsoft.Omnichannel.LiveChatWidget.SDK.closeChat()
                Microsoft.Omnichannel.LiveChatWidget.SDK.closeChat()  // twice bc *sometimes* the iframe only ends the chat but doesn't close
            }
            setChatBotOpen(false);
            setIsMinimized(false);

        }
    }, [props.loginStatus])

    useEffect(() => {

        window.addEventListener("lcw:ready", function handleLivechatReadyEvent() {
            // Handle LiveChat Ready event
            // SDK methods are ready for use now
            // Setting custom context provider to be used with Chat for Dynamics 365
            // The custom context provided by custom context provider can be used for routing the chat to a particular queue
            let logString = getLogString('ChatReady', false);
            if (logString) {
                logUI(logString, new Date(), Tools.getURLPath())
            }
        });

        window.addEventListener("lcw:error", (errorEvent) => {
            // Handle LiveChat SDK error event
            console.log('lcw:error', errorEvent);
            let logString = getLogString('ChatError', false);
            if (logString) {
                logUI(logString, new Date(), Tools.getURLPath(), JSON.stringify(errorEvent))
            }
        });

        window.addEventListener("StartChatMethodException", (e) => {
            console.log(e);
            let logString = getLogString('UnableToStartChat', false);
            if (logString) {
                logUI(logString, new Date(), Tools.getURLPath(), JSON.stringify(e))
            }
        });

        window.addEventListener("lcw:onMessageReceived", (e) => {
            handleNewMessage(e);
            window.dispatchEvent(clickEvent);
        });

        window.addEventListener("lcw:onMessageSent", debounce(handleSentMessage, 100));

        window.addEventListener("lcw:onMinimize", (e) => {
            setChatBotOpen(true); //if they minimize it must have been open. switching b/w hp and my clears out open state, so remark as open
            setIsMinimized(true);
            setUnseenMessages(0);
            window.dispatchEvent(clickEvent);
            let logString = getLogString('ChatMinimized', true);
            if (logString) {
                logUI(logString, new Date(), Tools.getURLPath())
            }
        });

        window.addEventListener("lcw:onMaximize", (e) => {
            window.dispatchEvent(clickEvent);
            let logString = getLogString('ChatMaximized', true);
            if (logString) {
                logUI(logString, new Date(), Tools.getURLPath())
            }
        });

        window.addEventListener("lcw:onClose", (e) => {
            setChatBotOpen(false);
            setIsMinimized(false);
            setUnseenMessages(0);
            window.dispatchEvent(clickEvent);
            let logString = getLogStringFromAction('ChatClosed')
            if (logString) {
                logUI(logString, new Date(), Tools.getURLPath())
            }
        });
        return function cleanup() {
            window.removeEventListener("lcw:ready", function handleLivechatReadyEvent() {
                let logString = getLogString('ChatReady', false);
                if (logString) {
                    logUI(logString, new Date(), Tools.getURLPath())
                }
            })

            window.removeEventListener("lcw:error", (errorEvent) => {
                // Handle LiveChat SDK error event
                console.log(errorEvent);
                let logString = getLogString('ChatError', false);
                if (logString) {
                    logUI(logString, new Date(), Tools.getURLPath(), JSON.stringify(errorEvent))
                }
            });

            window.removeEventListener("StartChatMethodException", (e) => {
                console.log(e);
                let logString = getLogString('UnableToStartChat', false);
                if (logString) {
                    logUI(logString, new Date(), Tools.getURLPath(), JSON.stringify(e))
                }
            });

            window.removeEventListener("lcw:onMessageReceived", (e) => {
                handleNewMessage(e);
                window.dispatchEvent(clickEvent);
            });

            window.removeEventListener("lcw:onMessageSent", debounce(handleSentMessage, 100));

            window.removeEventListener("lcw:onMinimize", (e) => {
                setIsMinimized(true);
                setChatBotOpen(true); //if they minimize it must have been open. switching b/w hp and my clears out open state, so remark as open
                setUnseenMessages(0);
                window.dispatchEvent(clickEvent);
                let logString = getLogString('ChatMinimized', true)
                if (logString) {
                    logUI(logString, new Date(), Tools.getURLPath())
                }
            });

            window.removeEventListener("lcw:onMaximize", (e) => {
                window.dispatchEvent(clickEvent);
                let logString = getLogString('ChatMinimized', true)
                if (logString) {
                    logUI(logString, new Date(), Tools.getURLPath())
                }
            });

            window.removeEventListener("lcw:onClose", (e) => {
                setChatBotOpen(false);
                setIsMinimized(false);
                setUnseenMessages(0);
                window.dispatchEvent(clickEvent);
                let logString = getLogStringFromAction('ChatClosed')
                if (logString) {
                    logUI(logString, new Date(), Tools.getURLPath())
                }
            });
        }
    }, []);

    function checkIfChatBotOpenForBusiness() {
        let currentTime = moment();
        let isOpenForBiz = chatBotStart <= currentTime && currentTime <= chatBotEnd
        setChatBotOpenForBusiness(isOpenForBiz)
        if (!isOpenForBiz && props.isActiveFeature_ChatBot) {
            //if the chat bot is not open for business, lets check again in the time that it will be! 
            let timeoutInSec = 60000 // 1 minute default
            if (currentTime <= chatBotStart) {
                timeoutInSec = chatBotStart.diff(currentTime);
            } else {
                timeoutInSec = chatBotStart.add(1, 'd').diff(currentTime);
            }
            setTimeout(checkIfChatBotOpenForBusiness, timeoutInSec)
        } else if (isOpenForBiz && props.isActiveFeature_ChatBot) {
            //if the chat bot is open for business, lets check again when it's time to close shop! 
            let timeoutInSec = 60000 // 1 minute default
            timeoutInSec = chatBotEnd.diff(currentTime);
            setTimeout(checkIfChatBotOpenForBusiness, timeoutInSec)
        }

    }


    function startChat(userClicked) {
        if (chatBotOpenForBusiness) {
            if (!chatBotOpen) {
                let contextProviderDetail = contextProvider();
                Microsoft.Omnichannel.LiveChatWidget.SDK.setContextProvider(contextProvider);
                let logString = getLogString('ContextProviderSent', userClicked)
                logUI(logString, new Date(), Tools.getURLPath(), JSON.stringify(contextProviderDetail));
            }
            let logString = getLogString('ChatOpened', userClicked)
            if (logString) {
                logUI(logString, new Date(), Tools.getURLPath())
            }
            setChatBotOpen(true);
            setIsMinimized(false);
            setUnseenMessages(0);
            Microsoft.Omnichannel.LiveChatWidget.SDK.startChat()
        }
    }

    function getLogString(action, userClicked) {
        let loc = refCrmSendResponse.current ? 'Supportrequestsent' : cleanLocationString(refLocation.current);
        let memberType = refIsSWHP.current ? 'SWHP' : refIsFC.current ? 'FC' : '';

        // make sure we are logging on health plan related screens only
        const locs = ['Support', 'Newsupportrequest', 'Supportrequestsent', 'Insurance', 'Benefits', 'Usage', 'Customerservice', 'Claims']

        if (refIsSWHP.current && locs.includes(loc)) {
            return `MBHWeb.UI.ChatBot.${action}.${userClicked ? 'UserClicked' : 'Auto'}.${loc}.${memberType}Member`
        } else if (refIsFC.current && locs.includes(loc)) {
            return `MBHWeb.UI.ChatBot.${action}.${userClicked ? 'UserClicked' : 'Auto'}.${loc}.${memberType}Member`
        }
    }

    function getLogStringFromAction(action) {
        let loc = refCrmSendResponse.current ? 'Supportrequestsent' : cleanLocationString(refLocation.current);
        let memberType = refIsSWHP.current ? 'SWHP' : refIsFC.current ? 'FC' : '';

        // make sure we are logging on health plan related screens only
        const locs = ['Support', 'Newsupportrequest', 'Supportrequestsent', 'Insurance', 'Benefits', 'Usage', 'Customerservice', 'Claims']

        if (refIsSWHP.current && locs.includes(loc)) {
            return `MBHWeb.UI.ChatBot.${action}.${loc}.${memberType}Member`
        } else if (refIsFC.current && locs.includes(loc)) {
            return `MBHWeb.UI.ChatBot.${action}.${loc}.${memberType}Member`
        }
    }

    function cleanLocationString(location) {
        return capitalize(location.replace('/', ''))
    }

    function contextProvider() {
        const { user, fcMemberId, swhpMemberId } = props;
        const name = `${user.firstName} ${user.lastName}`;
        const memberId = refIsSWHP.current ? refSWHPId.current : refIsFC.current ? refFCId.current : '';

        //Here it is assumed that the corresponding work stream would have context variables with logical name of 'contextKey1', 'contextKey2', 'contextKey3'. If no context variable exists with a matching logical name, items are created assuming Type:string
        return {
            'Name': { 'value': name, 'isDisplayable': true },
            'Email': { 'value': user.email, 'isDisplayable': true },
            'MemberId': { 'value': `${memberId}`, 'isDisplayable': true },
            'ChatType': { 'value': 'Member', 'isDisplayable': true }
        };
    }

    function checkLocation(location) {
        const allowedLocations = ['/insurance', '/usage', '/claims', '/benefits', '/support', '/customerservice', '/newSupportRequest']
        if (allowedLocations.includes(location)) {
            return true;
        }
        return false;
    }

    function openChatBotProactively(location) {
        const locations = ['/customerservice', '/support']
        if (locations.includes(location)) {
            return true;
        }
        return false;
    }

    function handleNewMessage(e) {
        if (e?.detail?.messageType === "user") {
            const msgId = e.detail.id;
            let isInMessagesArray = messagesArray.includes(msgId);

            if (!isInMessagesArray) {
                messagesArray.push(msgId);
                if (refIsMinimized.current) {
                    setUnseenMessages(unseenMessages => unseenMessages + 1);
                }
                setMessagesArray(messagesArray);
            }
        }
    };

    function handleSentMessage(e) {
        window.dispatchEvent(clickEvent);
        if (refIsFirstMessage.current) {
            setIsFirstMessage(false);
            let logString = getLogStringFromAction('FirstMessageFromUser');
            if (logString) {
                logUI(logString, new Date(), Tools.getURLPath())
            }
        } else {
            let logString = getLogStringFromAction('MessageFromUser');
            if (logString) {
                logUI(logString, new Date(), Tools.getURLPath())
            }
        }
    }

    return (
        <>
            {
                (showChatBotByLocation || chatBotOpen) && props.loggedIn && props.enableOmnichannelChatBot && props.isActiveFeature_ChatBot && chatBotOpenForBusiness &&
                <div id="chat-bot" className="chat-bot-container" onClick={(() => startChat(true))}>
                    <span className="chat-bot-icon">
                        <img className="chat-icon" alt="Chat" src="/images/chatBot/chat04.svg" />
                    </span>
                    <span className={`chat-bot-hover-ele ${chatBotOpen ? 'hiding' : ''}`}><span className="chat-bot-icon-text">Lets chat!</span></span>
                    {unseenMessages > 0 && <span className="chat-notification"><span className="chat-bot-unseen-messages">{unseenMessages}</span></span>}
                </div>
            }
        </>
    )
}


function mapStateToProps(state) {
    return {
        user: state?.memberships?.membershipData?.user,
        loggedIn: state?.domain?.li,
        loginStatus: state?.header?.loginStatus,
        isActiveFeature_ChatBot: state?.memberships?.isActiveFeature_ChatBot,
        enableOmnichannelChatBot: window.serverData.data.enableOmnichannelChatBot,
        omnichannelChatBotStart: window.serverData.data.omnichannelChatBotStart,
        omnichannelChatBotEnd: window.serverData.data.omnichannelChatBotEnd,
        crmMessages: state.healthPlanServices.crmMessages,
        crmIsFetching: state.healthPlanServices.isFetching, // tells us if crmMessages have loaded
        crmError: state.healthPlanServices.error, // tells us if crmMessages have tried to load and errored
        crmSendResponse: state.healthPlanServices.crmSendMessageResponse, // this exists if the user successfully submitted a member support ticket
        hasSWHPLinked: state.memberships.hasAnySWHP,
        swhpMemberId: state.memberships.anySWHPMembershipId,
        hasFCLinked: state.memberships.hasFC,
        fcMemberId: state.memberships.fcMembershipId,
    };
}



export default connect(mapStateToProps)(ChatBot);
